import { useEffect, useState } from 'react';
import { type SendToIntegrationContainerProps } from './useSendToIntegrationLogic';
import { getTemplates } from '../utils/clients/walnutApi';
import type { IntegrationConnection } from '../types';
import type { Template } from '../types';
import { handleAsyncState } from '../utils/handleAsyncState';

export const useGetConnectionsTemplates = (
  sendToIntegrationProps: SendToIntegrationContainerProps,
  connectedConnections: IntegrationConnection[]
) => {
  const [templatesByConnections, setTemplatesByConnections] = useState<
    Record<string, Template[]>
  >({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<Error | null>(null);

  useEffect(() => {
    if (!sendToIntegrationProps.userEnterpriseId) {
      return;
    }
    async function loadTemplates() {
      const bearerToken = await sendToIntegrationProps.authFn();

      // Load all templates for the enterpise
      const enterpriseTemplates = await getTemplates({
        apiHostname: sendToIntegrationProps.apiHostname,
        token: bearerToken,
        enterpriseId: sendToIntegrationProps.userEnterpriseId
      });

      const templates: Record<string, Template[]> = {};
      // Add templates into connections
      for (const connectionTemplate of enterpriseTemplates) {
        const connectionId = connectionTemplate.integration_connection_id;

        const activeConnection = connectedConnections.find(
          ({ id }) => id === connectionId
        );

        if (activeConnection) {
          templates[connectionId] = templates[connectionId] || [];

          templates[connectionId].push({
            templateId: connectionTemplate.id,
            project_id: connectionTemplate.project_id,
            issue_type_id: connectionTemplate.issue_type_id,
            name: connectionTemplate.name,
            fieldMappings: connectionTemplate.field_mappings,
            impactMappings: connectionTemplate.impact_mappings,
            connection: {
              ...activeConnection,
              baseUrl: activeConnection.base_url || '',
              templates: [],
              productSlug: activeConnection.product_slug
            }
          });
        }
      }

      setTemplatesByConnections(templates);
    }

    handleAsyncState(loadTemplates, setTemplatesLoading, setTemplatesError);
  }, [connectedConnections, sendToIntegrationProps.userEnterpriseId]);

  return { templatesByConnections, templatesLoading, templatesError };
};
